import React, { useState } from 'react';
import { useAlert } from '../../providers/AlertProvider';
import { uploadMultipleCustomerPaperwork } from '../../api/paperwork';
import '../css/UploadPdf.css'; // Reuse existing CSS
import logger from "../../utils/logger";


const CustomerPaperworkUpload = ({ 
    orderId, 
    onUploadComplete, 
    maxFiles = 5, 
    theme = 'light',
    disabled = false,
    isOrderPending = true 
}) => {
    const { showAlert } = useAlert();
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    const isDark = theme === 'dark';

    const validateFile = (file) => {
        // Check file type
        if (!file.type.includes('pdf')) {
            return `${file.name} is not a PDF file`;
        }
        
        // Check file size (10MB limit)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            return `${file.name} is too large. Maximum size is 10MB`;
        }
        
        return null;
    };

    const handleFileSelect = (event) => {
        const selectedFiles = Array.from(event.target.files);
        addFiles(selectedFiles);
    };

    const addFiles = (newFiles) => {
        const validFiles = [];
        const errors = [];

        newFiles.forEach(file => {
            const error = validateFile(file);
            if (error) {
                errors.push(error);
            } else if (files.length + validFiles.length < maxFiles) {
                validFiles.push(file);
            } else {
                errors.push(`Maximum ${maxFiles} files allowed. Cannot add ${file.name}`);
            }
        });

        if (errors.length > 0) {
            showAlert({
                message: errors.join('\n'),
                type: 'warning',
                buttons: [{ label: 'OK', type: 'primary' }]
            });
        }

        if (validFiles.length > 0) {
            setFiles(prev => [...prev, ...validFiles]);
        }
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (files.length === 0) {
            showAlert({
                message: 'Please select at least one PDF file to upload',
                type: 'warning',
                buttons: [{ label: 'OK', type: 'primary' }]
            });
            return;
        }

        if (!orderId) {
            showAlert({
                message: 'Order ID is required for paperwork upload',
                type: 'danger',
                buttons: [{ label: 'OK', type: 'primary' }]
            });
            return;
        }

        if (!isOrderPending) {
            showAlert({
                message: 'Paperwork can only be uploaded when the order is pending',
                type: 'warning',
                buttons: [{ label: 'OK', type: 'primary' }]
            });
            return;
        }

        setUploading(true);

        try {
            const response = await uploadMultipleCustomerPaperwork(files, orderId);

            if (response.success) {
                showAlert({
                    message: response.message,
                    type: response.hasErrors ? 'warning' : 'success',
                    buttons: [{ label: 'OK', type: 'primary' }]
                });

                if (onUploadComplete) {
                    onUploadComplete(response.data);
                }

                // Clear files on successful upload
                setFiles([]);
            } else {
                showAlert({
                    message: response.message || 'Failed to upload files',
                    type: 'danger',
                    buttons: [{ label: 'OK', type: 'primary' }]
                });
            }
        } catch (error) {
            logger.error('Upload error:', error);
            showAlert({
                message: 'An error occurred while uploading files',
                type: 'danger',
                buttons: [{ label: 'OK', type: 'primary' }]
            });
        } finally {
            setUploading(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (!disabled) {
            setDragOver(true);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        
        if (disabled) return;

        const droppedFiles = Array.from(e.dataTransfer.files);
        addFiles(droppedFiles);
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const containerStyle = {
        padding: '1rem',
        border: `2px dashed ${dragOver ? '#007bff' : (isDark ? '#555' : '#ddd')}`,
        borderRadius: '8px',
        backgroundColor: dragOver ? (isDark ? '#333' : '#f8f9fa') : (isDark ? '#2a2a2a' : '#fff'),
        color: isDark ? '#fff' : '#000',
        transition: 'all 0.3s ease',
        opacity: disabled || !isOrderPending ? 0.6 : 1,
        cursor: disabled || !isOrderPending ? 'not-allowed' : 'pointer'
    };

    const buttonStyle = {
        padding: '0.5rem 1rem',
        margin: '0.25rem',
        border: 'none',
        borderRadius: '4px',
        cursor: disabled || uploading || !isOrderPending ? 'not-allowed' : 'pointer',
        fontWeight: 'bold',
        opacity: disabled || uploading || !isOrderPending ? 0.6 : 1
    };

    const primaryButtonStyle = {
        ...buttonStyle,
        backgroundColor: '#007bff',
        color: '#fff'
    };

    const dangerButtonStyle = {
        ...buttonStyle,
        backgroundColor: '#dc3545',
        color: '#fff'
    };

    return (
        <div style={{ marginBottom: '1rem' }}>
            <h4 style={{ marginBottom: '0.5rem', color: isDark ? '#fff' : '#000' }}>
                Customer Paperwork Upload (Max {maxFiles} files)
            </h4>
            
            {/* Drop zone */}
            <div
                style={containerStyle}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !disabled && !isOrderPending ? null : document.getElementById('paperwork-file-input').click()}
            >
                <div style={{ textAlign: 'center', padding: '1rem' }}>
                    <p style={{ margin: '0.5rem 0', fontSize: '1.1rem' }}>
                        📄 {isOrderPending ? 'Drop PDF files here or click to select' : 'Paperwork upload only available for pending orders'}
                    </p>
                    {isOrderPending && (
                        <p style={{ margin: '0.5rem 0', fontSize: '0.9rem', opacity: 0.7 }}>
                            Maximum {maxFiles} files, 10MB each
                        </p>
                    )}
                </div>
                
                <input
                    id="paperwork-file-input"
                    type="file"
                    accept="application/pdf"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                    disabled={disabled || !isOrderPending}
                />
            </div>

            {/* File list */}
            {files.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                    <h5 style={{ marginBottom: '0.5rem', color: isDark ? '#fff' : '#000' }}>
                        Selected Files:
                    </h5>
                    <div style={{ 
                        border: `1px solid ${isDark ? '#555' : '#ddd'}`,
                        borderRadius: '4px',
                        backgroundColor: isDark ? '#333' : '#f8f9fa'
                    }}>
                        {files.map((file, index) => (
                            <div
                                key={index}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '0.75rem',
                                    borderBottom: index < files.length - 1 ? `1px solid ${isDark ? '#555' : '#eee'}` : 'none',
                                    color: isDark ? '#fff' : '#000'
                                }}
                            >
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                        {file.name}
                                    </div>
                                    <div style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                                        {formatFileSize(file.size)}
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeFile(index);
                                    }}
                                    style={{
                                        ...dangerButtonStyle,
                                        padding: '0.25rem 0.5rem',
                                        fontSize: '0.8rem'
                                    }}
                                    disabled={disabled || uploading || !isOrderPending}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Upload button */}
            {files.length > 0 && (
                <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                    <button
                        onClick={handleUpload}
                        style={primaryButtonStyle}
                        disabled={disabled || uploading || files.length === 0 || !isOrderPending}
                    >
                        {uploading ? 'Uploading...' : `Upload ${files.length} File${files.length !== 1 ? 's' : ''}`}
                    </button>
                </div>
            )}
        </div>
    );
};

export default CustomerPaperworkUpload;
